import * as Popover from '@radix-ui/react-popover'
import { CalendarCog, Clock, Info, Lock, Palmtree } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { EmptyState } from '@/components/ui/EmptyState'
import { FieldNote } from '@/components/ui/FieldNote'
import { Skeleton } from '@/components/ui/Skeleton'
import { Tooltip } from '@/components/ui/Tooltip'
import { toDecimal, toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { formatHmm, parseFlexibleHours, parseMoneyStrict, travelMessage } from '@/lib/hours'
import {
  usePickers,
  useSaveCell,
  useWeek,
  useWorkdayExceptions,
  type CellValues,
} from '@/lib/time-queries'
import { descriptionCopy, hoursMessage, MAX_DAY_HOURS } from '@/lib/timesheet-copy'
import type { Me, PickerData, WeekCell, WeekRow } from '@/lib/types'
import { cn } from '@/lib/utils'
import { dayStatus, weekMetaFromId } from '@/lib/week'
import { AddRowButton } from './AddRowButton'
import {
  closedOn,
  rowBoundary,
  rowsForDay,
  rowMenuState,
  selectionFromRow,
  type NewRowSelection,
  type RowMenuState,
} from './week-rows'
import { DayHeaderPopover } from './DayHeaderPopover'
import { DayNav, DayTotalsStrip } from './DayNav'
import { FreeDayBanner } from './FreeDayBanner'

import { RowActions } from './RowActions'
import { RowLabelEditable } from './RowLabelEditable'
import { StoplichtDot } from './StoplichtDot'
import { TimesheetToolbar } from './TimesheetToolbar'
import { UtilizationStrip } from './UtilizationStrip'
import { WeekApprovalControl } from './WeekApprovalControl'
import type { TimesheetRows } from './useTimesheetRows'

interface DayViewProps {
  weekId: string
  dayIndex: number
  /**
   * Week en dag in een keer: een stap over een weekgrens verandert ze allebei, en wie
   * ze los zou melden zet onderweg een combinatie neer die niet bestaat.
   */
  onNavigate: (weekId: string, dayIndex: number) => void
  personId?: string
  me?: Me
  editable: boolean
  viewControl?: ReactNode
  /** Extra knoppen in het doen-cluster, naast de dagacties en het week-akkoord. */
  actions?: ReactNode
  sheet: TimesheetRows
}

export function DayView({
  weekId,
  dayIndex,
  onNavigate,
  personId,
  me,
  editable,
  viewControl,
  actions,
  sheet,
}: DayViewProps) {
  const meta = weekMetaFromId(weekId)
  const weekQuery = useWeek(weekId, personId)
  const pickersQuery = usePickers(personId)
  const saveCell = useSaveCell(personId, me)
  const exceptionsQuery = useWorkdayExceptions(meta.days[0].iso, meta.days[6].iso, personId, editable)

  const day = meta.days[dayIndex]
  const week = weekQuery.data
  // Een afgesloten week is een slot: de server weigert mutaties tot de week is heropend.
  const weekClosed = week?.approval?.status === 'APPROVED'
  const dayIsClosed = closedOn(week?.approval, meta.days[dayIndex].iso)
  /**
   * Tijdens een wissel van week of persoon houdt react-query de vorige uitslag in
   * beeld. Die dagtotalen en dat akkoord horen bij een andere week: typen zou op de
   * verkeerde regel landen en afsluiten zou de verkeerde week beoordelen.
   */
  const stale = weekQuery.isPlaceholderData
  const canEdit = editable && !weekClosed && !dayIsClosed && !stale
  const dayInfo = week?.dailyTotals.find((d) => d.dayIndex === dayIndex)
  const dayTotal = toNumber(dayInfo?.total)
  const freeDay = dayInfo?.dayType === 'vacation' || dayInfo?.dayType === 'holiday'
  const exception = (exceptionsQuery.data ?? []).find((e) => e.date === day.iso) ?? null

  const { rows: weekRows, localKeysForDay, savedRowKeys, landedRowKey, addRow, dropRow, replaceRow, alignRow, markRowSaved, clearLanded } = sheet

  // De dagweergave gaat over deze dag: regels die alleen op een andere dag van de week uren
  // hebben, horen hier niet als lege regel te staan. Ze komen terug als suggestie-chip.
  const localKeys = localKeysForDay(dayIndex)
  const rows = rowsForDay(weekRows, localKeys, dayIndex)

  // Een regel die je hier neerzet hoort bij deze dag; op een andere dag hoort hij niet
  // als lege regel mee te lopen.
  const addRowOnDay = useCallback((selection: NewRowSelection) => addRow(selection, dayIndex), [addRow, dayIndex])

  // Het dagtotaal onderaan telt mee wat er getypt is maar nog niet is opgeslagen. Zonder
  // dat zou een rood urenveld boven de 24-uursgrens nergens in beeld verklaard worden:
  // het veld zelf is te smal voor een melding en er hoort geen blok onder de rij.
  const [draftHours, setDraftHours] = useState<Record<string, number>>({})
  const reportDraftHours = useCallback((rowKey: string, hours: number | null) => {
    setDraftHours((prev) => {
      if (hours === null) {
        if (!(rowKey in prev)) return prev
        const { [rowKey]: _dropped, ...rest } = prev
        return rest
      }
      return prev[rowKey] === hours ? prev : { ...prev, [rowKey]: hours }
    })
  }, [])
  const draftDelta = rows.reduce((sum, row) => {
    const typed = draftHours[row.rowKey]
    return typed === undefined ? sum : sum + typed - toNumber(row.cells[dayIndex].hours)
  }, 0)
  const shownDayTotal = dayTotal + draftDelta
  const overDayTotal = shownDayTotal > MAX_DAY_HOURS

  // Een net toegevoegde regel krijgt de focus zodat je meteen uren kunt typen, en dooft
  // daarna zijn markering uit.
  const landedIndex = rows.findIndex((row) => row.rowKey === landedRowKey)
  // biome-ignore lint/correctness/useExhaustiveDependencies: landedRowKey blijft in de lijst zodat een volgende landing op dezelfde index de focus en de timer opnieuw zet
  useEffect(() => {
    if (landedIndex < 0) return
    document.getElementById(`dayview-${landedIndex}-hours`)?.focus()
    const timer = window.setTimeout(clearLanded, 900)
    return () => window.clearTimeout(timer)
  }, [landedIndex, landedRowKey, clearLanded])

  const emptyWorkdays = (week?.dailyTotals ?? [])
    .filter((d) => d.expectedWorkday && toNumber(d.total) === 0)
    .map((d) => `${meta.days[d.dayIndex].short.toLowerCase()} ${meta.days[d.dayIndex].date.getDate()}`)

  function saveCellValues(row: WeekRow, values: CellValues) {
    const cell = row.cells[dayIndex]
    // De server zet een nieuwe boeking op de eerstvolgende plek van deze dag; een verse
    // regel die een ander slot raadde hoort daar eerst naartoe.
    const rowKey = cell.entryId === null ? alignRow(row.rowKey, dayIndex) : row.rowKey
    // Vanaf nu bestaat er invoer voor deze regel; een rijwissel moet naar de server.
    markRowSaved(rowKey)
    saveCell.mutate({
      rowKey,
      dayIndex,
      date: cell.date,
      projectId: row.projectId,
      categoryId: row.categoryId,
      entryId: cell.entryId,
      values,
    })
  }

  const entryPanel = pickersQuery.data ? (
    <AddRowButton
      pickers={pickersQuery.data}
      onAdd={addRowOnDay}
      variant={rows.length === 0 ? 'hero' : 'inline'}
    />
  ) : (
    <span className="text-sm text-ink-faint">Projecten laden…</span>
  )

  return (
    <div className="space-y-4">
      <TimesheetToolbar
        nav={
          <>
            <DayNav weekId={weekId} dayIndex={dayIndex} onChange={onNavigate} />
            {viewControl}
          </>
        }
        readout={
          <>
            <span className="flex items-center gap-2 text-sm text-ink-faint">
              {freeDay && dayTotal === 0 ? (
                <Palmtree className="h-4 w-4" aria-hidden="true" />
              ) : (
                <StoplichtDot status={dayStatus(dayTotal, dayInfo?.expectedWorkday ?? day.isWorkday)} />
              )}
              {meta.label}
            </span>
            {week?.dailyTotals?.length ? (
              <DayTotalsStrip
                weekId={weekId}
                totals={week.dailyTotals}
                dayIndex={dayIndex}
                onSelect={(index) => onNavigate(weekId, index)}
              />
            ) : null}
          </>
        }
        actions={
          <>
            {actions}
            {canEdit && (dayInfo ? dayInfo.dayType !== 'weekend' : day.isWorkday) ? (
              <DayHeaderPopover
                weekId={weekId}
                personId={personId}
                date={day.iso}
                short={day.short}
                dayNumber={day.date.getDate()}
                isToday={false}
                dayType={dayInfo?.dayType ?? (day.isWorkday ? 'workday' : 'weekend')}
                dayLabel={dayInfo?.dayLabel ?? null}
                exception={exception}
                editable={canEdit}
              >
                <span className="btn-secondary h-8 w-8 justify-center p-0" aria-label="Dagacties" title="Dagacties">
                  <CalendarCog className="h-4 w-4" />
                </span>
              </DayHeaderPopover>
            ) : null}
            {week && !stale ? (
              <WeekApprovalControl
                approval={week.approval}
                weekId={weekId}
                personId={personId}
                editable={editable}
                weekLabel={meta.label}
                emptyWorkdays={emptyWorkdays}
              />
            ) : null}
          </>
        }
      />

      {week?.utilization ? (
        <UtilizationStrip personId={personId} weekId={weekId} utilization={week.utilization} dailyTotals={week.dailyTotals} />
      ) : null}

      {freeDay ? (
        <FreeDayBanner
          weekId={weekId}
          personId={personId}
          dayIso={day.iso}
          dayInfo={dayInfo}
          exception={exception}
          editable={canEdit}
        />
      ) : null}

      {!weekQuery.isLoading && rows.length === 0 ? (
        <EmptyState
          icon={Clock}
          title={`Nog geen uren op ${day.label.toLowerCase()}`}
          description="Kies waar je aan werkte."
          action={canEdit ? entryPanel : undefined}
          className="w-full"
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-card bg-surface shadow-card">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-hairline">
                    <th className="label-caps border-l-2 border-l-transparent px-4 py-3 text-left">Klant · Project · Categorie</th>
                    <th className="label-caps w-24 px-2 py-3 text-right">Uren</th>
                    <th className="label-caps w-full px-3 py-3 text-left">Omschrijving</th>
                    <th className="label-caps w-24 px-2 py-3 text-right">Reiskosten</th>
                    <th className="w-16 px-2 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {weekQuery.isLoading ? (
                    <SkeletonRows />
                  ) : (
                    rows.map((row, rowIdx) => {
                      const { firstOfClient, isContinuation } = rowBoundary(row, rows[rowIdx - 1])
                      const pickerData = pickersQuery.data
                      // Net als verwijderen werkt de wissel op de hele weekregel, ook al toont
                      // deze weergave maar een dag.
                      const menu = rowMenuState(row, canEdit, pickerData, savedRowKeys.has(row.rowKey))
                      return (
                        <DayRow
                          key={`${row.rowKey}:${day.iso}`}
                          row={row}
                          cell={row.cells[dayIndex]}
                          rowIdx={rowIdx}
                          weekId={weekId}
                          personId={personId}
                          firstOfClient={firstOfClient}
                          isContinuation={isContinuation}
                          landed={row.rowKey === landedRowKey}
                          editable={canEdit}
                          saved={savedRowKeys.has(row.rowKey)}
                          otherHoursOnDay={dayTotal - toNumber(row.cells[dayIndex].hours)}
                          menu={menu}
                          pickers={pickerData}
                          onLocalCommit={(selection) => replaceRow(row.rowKey, selection, dayIndex)}
                          onCopyRow={() => addRowOnDay(selectionFromRow(row))}
                          onAddRow={addRowOnDay}
                          onDropLocalRow={() => dropRow(row.rowKey)}
                          onSave={(values) => saveCellValues(row, values)}
                          onDraftHours={reportDraftHours}
                        />
                      )
                    })
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t border-hairline-strong bg-fill-1 text-sm">
                    <td className="border-l-2 border-l-transparent px-4 py-3 font-medium text-ink">Dagtotaal</td>
                    <td
                      className={cn(
                        'num px-2 py-3 text-right font-semibold transition-colors duration-150',
                        overDayTotal ? 'text-status-overdue' : 'text-ink',
                      )}
                    >
                      {formatHmm(shownDayTotal)}
                    </td>
                    <td className="px-3 py-3" colSpan={2}>
                      <div className="flex items-center gap-2">
                        {freeDay && shownDayTotal === 0 ? (
                          <Palmtree className="h-3.5 w-3.5 text-ink-faint" aria-hidden="true" />
                        ) : (
                          <StoplichtDot status={dayStatus(shownDayTotal, dayInfo?.expectedWorkday ?? day.isWorkday)} />
                        )}
                        {overDayTotal ? (
                          <span className="text-xs font-medium text-status-overdue">
                            Meer dan {MAX_DAY_HOURS} uur op een dag
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {canEdit && !weekQuery.isLoading ? entryPanel : null}
          {editable && (weekClosed || dayIsClosed) ? (
            <p className="text-sm text-ink-faint">
              {weekClosed
                ? 'Week is afgesloten - heropen de week om uren te wijzigen.'
                : 'Deze dag is met de maand afgesloten - heropen de week om de uren te wijzigen.'}
            </p>
          ) : null}
        </>
      )}
    </div>
  )
}

interface DayRowProps {
  row: WeekRow
  cell: WeekCell
  rowIdx: number
  weekId: string
  personId?: string
  firstOfClient: boolean
  isContinuation: boolean
  landed: boolean
  editable: boolean
  /** Er is voor deze regel al weggeschreven, ook al zijn de entry-ids nog niet binnen. */
  saved: boolean
  otherHoursOnDay: number
  menu: RowMenuState
  pickers: PickerData | undefined
  onLocalCommit: (selection: NewRowSelection) => void
  onCopyRow: () => void
  onAddRow: (selection: NewRowSelection) => void
  onDropLocalRow: () => void
  onSave: (values: CellValues) => void
  /** Meldt getypte uren aan de tabel zolang ze afwijken van wat er is opgeslagen. */
  onDraftHours: (rowKey: string, hours: number | null) => void
}

function draftFromCell(cell: WeekCell): { hours: string; description: string } {
  return {
    hours: toNumber(cell.hours) > 0 ? formatHmm(toNumber(cell.hours)) : '',
    description: cell.description,
  }
}

function DayRow({
  row,
  cell,
  rowIdx,
  weekId,
  personId,
  firstOfClient,
  isContinuation,
  landed,
  editable,
  saved,
  otherHoursOnDay,
  menu,
  pickers,
  onLocalCommit,
  onCopyRow,
  onAddRow,
  onDropLocalRow,
  onSave,
  onDraftHours,
}: DayRowProps) {
  const [draft, setDraft] = useState(() => draftFromCell(cell))
  const [synced, setSynced] = useState({ hours: cell.hours, description: cell.description })
  const [saveAttempted, setSaveAttempted] = useState(false)
  const lastSubmitted = useRef<string | null>(null)
  const reverting = useRef(false)

  if (cell.hours !== synced.hours || cell.description !== synced.description) {
    setSynced({ hours: cell.hours, description: cell.description })
    setDraft(draftFromCell(cell))
    lastSubmitted.current = null
  }

  const parsedHours = draft.hours.trim() === '' ? 0 : parseFlexibleHours(draft.hours)
  const invalidHours = parsedHours === null
  const enteredHours = parsedHours ?? 0
  const prospectiveDayTotal = otherHoursOnDay + enteredHours
  const overDayLimit = prospectiveDayTotal > MAX_DAY_HOURS
  const hoursError = hoursMessage(invalidHours, prospectiveDayTotal)
  const isNewEntry = cell.entryId === null
  const hadDescription = cell.description.trim() !== ''
  const descriptionMissing = enteredHours > 0 && draft.description.trim() === '' && (isNewEntry || hadDescription)
  const server = draftFromCell(cell)
  const dirty = draft.hours.trim() !== server.hours || draft.description !== server.description
  const canSave = !invalidHours && !overDayLimit && !descriptionMissing
  // Alleen als de omschrijving de laatste blokkade is: staat het dagtotaal ook boven de
  // grens, dan wijst "niet opgeslagen" de verkeerde oorzaak aan.
  const onlyDescriptionMissing = descriptionMissing && !invalidHours && !overDayLimit
  const blocked = onlyDescriptionMissing && saveAttempted

  if (saveAttempted && !descriptionMissing) setSaveAttempted(false)

  const hoursDirty = draft.hours.trim() !== server.hours
  const rowKey = row.rowKey
  useEffect(() => {
    onDraftHours(rowKey, hoursDirty ? enteredHours : null)
    return () => onDraftHours(rowKey, null)
  }, [rowKey, hoursDirty, enteredHours, onDraftHours])

  function commit() {
    if (!dirty) return
    if (enteredHours <= 0 && isNewEntry) return
    // De regel schrijft niet weg, en er is geen opslaanknop die dat kan vertellen. Dus
    // krijgt het veld hier zijn attentie-toon, in plaats van dat de uren stil blijven staan.
    if (onlyDescriptionMissing) {
      setSaveAttempted(true)
      return
    }
    if (!canSave) return
    const payloadKey = `${toDecimal(enteredHours)}|${draft.description.trim()}`
    if (lastSubmitted.current === payloadKey) return
    lastSubmitted.current = payloadKey
    onSave({
      hours: toDecimal(enteredHours),
      description: draft.description.trim(),
      travelAmount: cell.travelAmount,
    })
  }

  function revert() {
    reverting.current = true
    setDraft(draftFromCell(cell))
  }

  function handleRowBlur(event: React.FocusEvent<HTMLTableRowElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node)) return
    if (reverting.current) {
      reverting.current = false
      return
    }
    commit()
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      commit()
      // Enter betekent "volgende stap", en dat is hier de omschrijving en niet de rij eronder.
      if (onlyDescriptionMissing) {
        document.getElementById(`dayview-${rowIdx}-desc`)?.focus()
        return
      }
      const next = document.getElementById(`dayview-${rowIdx + 1}-hours`)
      next?.focus()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      revert()
      ;(event.target as HTMLInputElement).blur()
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const target = event.target as HTMLInputElement
      if (!target.id.endsWith('-hours')) return
      const next = document.getElementById(`dayview-${rowIdx + (event.key === 'ArrowDown' ? 1 : -1)}-hours`)
      if (next) {
        event.preventDefault()
        next.focus()
      }
    }
  }

  const label = (
    <RowLabelEditable
      row={row}
      firstOfClient={firstOfClient}
      isContinuation={isContinuation}
      menu={menu}
      pickers={pickers}
      weekId={weekId}
      personId={personId}
      onLocalCommit={onLocalCommit}
    />
  )
  const blockStart = firstOfClient && rowIdx > 0

  if (cell.locked || !editable) {
    return (
      <tr className={cn('group border-b border-hairline', blockStart && 'border-t border-hairline-strong')}>
        <td className="whitespace-nowrap border-l-2 border-l-transparent px-4 py-2.5">{label}</td>
        <td className="px-2 py-2.5 text-right">
          <Tooltip content={cell.locked ? (cell.invoiceNumber ? `Gefactureerd op ${cell.invoiceNumber}` : 'Gefactureerd') : null}>
            <span className="inline-flex items-center gap-1 text-ink-faint">
              {cell.locked ? <Lock className="h-3 w-3" /> : null}
              <span className="num text-sm">{toNumber(cell.hours) > 0 ? formatHmm(toNumber(cell.hours)) : '·'}</span>
            </span>
          </Tooltip>
        </td>
        <td className="px-3 py-2.5 text-sm text-ink-soft">{cell.description}</td>
        <td className="money px-2 py-2.5 text-right text-sm text-ink-faint">
          {cell.travelAmount ? formatEuros(toNumber(cell.travelAmount)) : ''}
        </td>
        <td className="px-2 py-2.5" />
      </tr>
    )
  }

  return (
    <tr
      className={cn(
        'group border-b border-hairline transition-colors duration-700 ease-spring',
        blockStart && 'border-t border-hairline-strong',
        landed && 'bg-accent-600/6',
      )}
      onBlur={handleRowBlur}
    >
      {/* De rand hoort op de cel: de rij voert al een trage kleurovergang voor de landing. */}
      <td
        className={cn(
          'whitespace-nowrap border-l-2 px-4 py-2.5 transition-colors duration-150',
          blocked ? 'border-l-status-overdue' : 'border-l-transparent',
        )}
      >
        {label}
      </td>
      <td className="px-2 py-1.5 text-right">
        {/* Het veld is 80px breed en op dit moment gevuld, dus er past geen notitie in.
            De rode rand draagt het signaal; de zin hangt in de tooltip en in de sr-only
            tekst, en het dagtotaal onderaan verklaart de 24-uursgrens. */}
        <Tooltip content={hoursError}>
          <input
            id={`dayview-${rowIdx}-hours`}
            value={draft.hours}
            onChange={(event) => setDraft((d) => ({ ...d, hours: event.target.value }))}
            onKeyDown={handleKeyDown}
            placeholder="0:00"
            aria-label="Uren"
            aria-invalid={hoursError !== null}
            aria-describedby={hoursError ? `dayview-${rowIdx}-hours-note` : undefined}
            className={cn(
              'field num h-9 w-20 text-right transition-colors duration-150',
              hoursError !== null && 'border-status-overdue focus:border-status-overdue',
            )}
          />
        </Tooltip>
        <span id={`dayview-${rowIdx}-hours-note`} className="sr-only">
          {hoursError ?? ''}
        </span>
      </td>
      <td className="px-3 py-1.5">
        <div className="relative">
          <input
            id={`dayview-${rowIdx}-desc`}
            value={draft.description}
            onChange={(event) => setDraft((d) => ({ ...d, description: event.target.value }))}
            onKeyDown={handleKeyDown}
            placeholder="Waar werkte je aan?"
            aria-label="Omschrijving"
            aria-required={descriptionMissing}
            aria-invalid={blocked}
            aria-describedby={descriptionMissing ? `dayview-${rowIdx}-desc-req` : undefined}
            className={cn(
              'field h-9 w-full transition-colors duration-150',
              // Ruimte voor het label, en niet meer dan dat: de placeholder mag er tot
              // ver in de smalle breedtes naast blijven passen.
              descriptionMissing && (blocked ? 'pr-28' : 'pr-16'),
              // Zonder de focus-variant wint de focus-rand van .field precies op het
              // moment dat de escalatie gebeurt, want die zet de focus in dit veld.
              blocked && 'border-status-overdue focus:border-status-overdue',
            )}
          />
          {descriptionMissing ? (
            <FieldNote tone={blocked ? 'attention' : 'muted'}>
              {blocked ? descriptionCopy.blocked : descriptionCopy.required}
            </FieldNote>
          ) : null}
          <span id={`dayview-${rowIdx}-desc-req`} className="sr-only">
            {descriptionCopy.requiredHint}
          </span>
          {/* Leeg gemonteerd, anders kondigt een schermlezer de omslag niet aan. */}
          <span role="status" aria-live="polite" className="sr-only">
            {blocked ? descriptionCopy.blockedMessage : ''}
          </span>
        </div>
      </td>
      <td className="px-2 py-1.5 text-right">
        <TravelButton row={row} cell={cell} rowIdx={rowIdx} enteredHours={enteredHours} draftDescription={draft.description} onSave={onSave} />
      </td>
      <td className="px-2 py-1.5">
        <RowActions
          row={row}
          weekId={weekId}
          personId={personId}
          saved={saved}
          draftHours={hoursDirty ? enteredHours : undefined}
          dayIndex={cell.dayIndex}
          onCopyRow={onCopyRow}
          onAddRow={onAddRow}
          pickers={pickers}
          onDropLocalRow={onDropLocalRow}
        />
      </td>
    </tr>
  )
}

interface TravelButtonProps {
  row: WeekRow
  cell: WeekCell
  rowIdx: number
  enteredHours: number
  draftDescription: string
  onSave: (values: CellValues) => void
}


function TravelButton({ row, cell, rowIdx, enteredHours, draftDescription, onSave }: TravelButtonProps) {
  const [open, setOpen] = useState(false)
  const [travel, setTravel] = useState('')
  const hasTravel = toNumber(cell.travelAmount) > 0
  // Reiskosten horen bij een regel met uren, dus opslaan kan pas als die er staan. De
  // afspraak met de klant en het vorige bedrag zijn juist daarvoor al nuttig, dus het
  // paneel gaat wel open.
  const needsHours = enteredHours <= 0
  const parsedTravel = travel.trim() === '' ? 0 : parseMoneyStrict(travel)
  const travelError = travelMessage(parsedTravel)

  /**
   * Vult het reiskostenbedrag pas voor bij openen, en alleen als er nog niets staat.
   * Een leeg veld verwijdert bij opslaan juist een bestaande reiskost.
   */
  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (next) {
      if (cell.travelAmount) setTravel(String(toNumber(cell.travelAmount)).replace('.', ','))
      else if (row.lastTravelAmount !== null) setTravel(String(toNumber(row.lastTravelAmount)).replace('.', ','))
      else setTravel('')
    }
  }

  function submit() {
    if (needsHours || travelError !== null) return
    setOpen(false)
    onSave({
      hours: toDecimal(enteredHours),
      description: draftDescription.trim(),
      travelAmount: travel.trim() === '' ? null : toDecimal(parsedTravel ?? 0),
    })
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        {hasTravel ? (
          <button
            type="button"
            className="money rounded-pill bg-fill-2 px-2 py-0.5 text-xs font-medium text-ink-soft hover:bg-fill-3"
            aria-label="Reiskosten wijzigen"
          >
            {formatEuros(toNumber(cell.travelAmount))}
          </button>
        ) : (
          <button
            type="button"
            className="rounded-sm px-1.5 py-0.5 text-xs font-medium text-ink-faint transition-colors duration-150 hover:text-accent-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
            aria-label="Reiskosten toevoegen"
          >
            + €
          </button>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-40 w-56 rounded-card border border-hairline bg-surface p-3 shadow-pop animate-fade-in focus:outline-hidden"
        >
          <label htmlFor={`dayview-${rowIdx}-travel`} className="mb-1 block text-xs font-medium text-ink-soft">
            Reiskosten €
          </label>
          <Tooltip content={travelError}>
            <input
              id={`dayview-${rowIdx}-travel`}
              inputMode="decimal"
              value={travel}
              onChange={(event) => setTravel(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  submit()
                }
              }}
              placeholder="0,00"
              aria-invalid={travelError !== null}
              aria-describedby={travelError ? `dayview-${rowIdx}-travel-note` : undefined}
              className={cn(
                'field num text-right transition-colors duration-150',
                travelError !== null && 'border-status-overdue focus:border-status-overdue',
              )}
            />
          </Tooltip>
          <span id={`dayview-${rowIdx}-travel-note`} className="sr-only">
            {travelError ?? ''}
          </span>
          {row.travelAgreement ? (
            <p className="mt-1 flex items-start gap-1 text-xs text-ink-faint">
              <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              <span>{row.travelAgreement}</span>
            </p>
          ) : null}
          {row.lastTravelAmount !== null ? (
            <p className="mt-1 text-xs text-ink-faint">
              Vorige keer bij {row.clientName}: {formatEuros(toNumber(row.lastTravelAmount))}
            </p>
          ) : null}
          {needsHours ? <p className="mt-2 text-xs text-ink-faint">Vul eerst uren in op deze regel.</p> : null}
          <button
            type="button"
            onClick={submit}
            disabled={needsHours || travelError !== null}
            className="btn-primary mt-2 h-8 w-full disabled:cursor-not-allowed disabled:opacity-50"
          >
            Opslaan
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, r) => (
        <tr key={r} className="border-b border-hairline">
          <td className="border-l-2 border-l-transparent px-4 py-3">
            <Skeleton className="h-4 w-40" />
          </td>
          <td className="px-2 py-3">
            <Skeleton className="ml-auto h-6 w-16" />
          </td>
          <td className="px-3 py-3">
            <Skeleton className="h-6 w-full" />
          </td>
          <td className="px-2 py-3">
            <Skeleton className="ml-auto h-4 w-10" />
          </td>
          <td className="px-2 py-3" />
        </tr>
      ))}
    </>
  )
}
